/**
 * Identifies a role assignment a user holds. There are two kinds:
 *
 * - `named`    a workspace role backed by a Mongo `Role` record. The `id`
 *              is the Mongo ObjectId of that record. `workspaceId` is
 *              redundant given the global uniqueness of the id, but is
 *              carried so the ref alone round-trips through the OpenFGA
 *              key without a DB lookup.
 * - `channel`  a pseudo-role granting access to a single channel. Not
 *              backed by Mongo, and identified by the channel id.
 *
 * There is no `everyone` variant. Workspace-wide baseline access is granted
 * through the `workspace#participant` userset (see
 * {@link participantGrantKey}) rather than a `Role` record, even though both
 * clients present "everyone" as a role to pick
 * (docs/adr/11-workspace-participation-permission-structure.md), and
 * workspace-wide *delivery* addressing is the `everyone`
 * {@link AudienceMarker}. A marker carries no OpenFGA presence, so neither is
 * an `ElasoRoleRef`.
 *
 * Every variant carries `workspaceId` so the OpenFGA role key
 * (`workspace_role:{workspaceId}--…`) can be derived from the ref without
 * any DB lookup, and conversely every key parses to a ref with full context.
 */
export type ElasoRoleRef =
	| { kind: "named"; workspaceId: string; id: string }
	| { kind: "channel"; workspaceId: string; channelId: string };

/**
 * The caller's effective access level on an asset, collapsed from the set of
 * granted permissions to a single rung of the ladder. `null` means no access.
 * Shared so the SERVER's per-caller resolution (`channel.list` `selfPermission`)
 * and any CLIENT gating agree on the exact ladder.
 */
export type AssetAccessLevel = "read" | "interact" | "moderate";

/**
 * Collapse a set of granted permission strings to the highest {@link
 * AssetAccessLevel}. Mirrors the ladder the client used to compute in
 * `derivePermissionLevel` (`read_only` → `"read"`), moved here so server and
 * client never drift. Anything outside the three rungs collapses to `null`.
 */
export const collapseToAccessLevel = (
	permissions: Iterable<string>
): AssetAccessLevel | null => {
	const set = permissions instanceof Set ? permissions : new Set(permissions);
	if (set.has("moderate")) return "moderate";
	if (set.has("interact")) return "interact";
	if (set.has("read_only")) return "read";
	return null;
};

/**
 * Render a role ref as its OpenFGA `workspace_role:*` key. The format is
 * self-describing, so every ref kind parses back through the inverse routine on
 * the server. Shared between the client (channel-permission lookups) and the
 * server (OpenFGA writes) so the two never drift.
 */
export const getRoleKey = (ref: ElasoRoleRef): string => {
	switch (ref.kind) {
		case "named":
			return `workspace_role:${ref.workspaceId}--${ref.id}`;
		case "channel":
			return `workspace_role:${ref.workspaceId}--channel--${ref.channelId}`;
	}
};

/**
 * A symbolic addressee for a broadcast or `@mention`. Distinct from
 * {@link ElasoRoleRef}, because a marker grants no access and has no OpenFGA
 * presence. It is a server-side name that resolves to a concrete Audience at
 * delivery time, and it covers every way a broadcast can be addressed:
 *
 * - `role`     a single role's Audience.
 * - `everyone` every Participant of the workspace.
 * - `asset`    every role with access to a given channel or document asset,
 *              resolved through the asset-to-roles projection.
 * - `active`   every connection that currently has the asset OPEN on screen,
 *              declared through `asset.setActive`, and a strict subset of
 *              `asset` (the subordination invariant of
 *              docs/adr/23-the-live-lesson-delivery-tier.md). Only for events
 *              whose effect is reconstructed by opening the asset.
 * - `user`     a single user's own live connections, the one variant that
 *              resolves without touching the role layer.
 *
 * No variant carries authorization presence. Markers only address delivery.
 * See `Audience Marker` in `CONTEXT.md`.
 */
export type AudienceMarker =
	| { kind: "role"; ref: ElasoRoleRef }
	| { kind: "everyone"; workspaceId: string }
	| { kind: "asset"; assetKey: string }
	| { kind: "active"; assetKey: string }
	| { kind: "user"; firebaseUid: string };

/**
 * Address a single asset (channel or document) for delivery. The asset's authz
 * key is also its WS pub/sub topic name
 * (docs/adr/18-how-delivery-works.md), so an `asset` marker resolves on the
 * publish path, where the topic's subscriber set *is* the Audience, rather than
 * enumerating roles, then users, then connections. Replaces the former
 * `rolesToMarkers(getAssetRoles(assetKey))` idiom at channel and document
 * broadcast sites.
 */
export const assetMarker = (assetKey: string): AudienceMarker => ({
	kind: "asset",
	assetKey,
});

/**
 * Address the asset's *current viewers* only
 * (docs/adr/23-the-live-lesson-delivery-tier.md). Resolves on the publish path
 * over the `active:{assetKey}` topic, whose subscriber set the `asset.setActive`
 * declare maintains, and which is always a subset of the eager `asset` topic.
 * Admissible only for events that are ephemeral or refetched on asset open,
 * typing today. Anything a non-viewer consumes stays on `asset`.
 */
export const activeMarker = (assetKey: string): AudienceMarker => ({
	kind: "active",
	assetKey,
});

export const rolesToMarkers = (roles: ElasoRoleRef[]): AudienceMarker[] =>
	roles.map((ref) => ({ kind: "role", ref }));

export const roleRefEquals = (a: ElasoRoleRef, b: ElasoRoleRef): boolean => {
	if (a.kind !== b.kind) return false;
	if (a.workspaceId !== (b as { workspaceId: string }).workspaceId)
		return false;
	switch (a.kind) {
		case "named":
			return a.id === (b as { id: string }).id;
		case "channel":
			return a.channelId === (b as { channelId: string }).channelId;
	}
};

/**
 * Build the OpenFGA "participant baseline grant" key,
 * `workspace:{tenant}--{workspaceId}#participant`. A baseline channel grants
 * access to every Participant of its workspace through this userset, which is
 * directly assignable on each asset-permission relation (TECH-3082). It is NOT
 * a role key. It carries participation, not a role assignment, so no
 * `ElasoRoleRef` is ever derived from it.
 *
 * Shared so the server (asset-permission writes, keyed by `OPENFGA_TENANT_ID`)
 * and the client (channel-permission combobox, keyed by
 * `VITE_OPENFGA_TENANT_ID`) produce the same key and never drift.
 */
export const participantGrantKey = (
	tenantId: string,
	workspaceId: string
): string => `workspace:${tenantId}--${workspaceId}#participant`;

/**
 * Inverse of {@link participantGrantKey}. Returns the workspaceId if `key` is a
 * participant grant-key for `tenantId`, otherwise `null`. Pure string ops,
 * because a workspaceId is a Mongo id with no `--`, so stripping the prefix and
 * suffix is unambiguous.
 */
export const parseParticipantGrantKey = (
	tenantId: string,
	key: string
): string | null => {
	const prefix = `workspace:${tenantId}--`;
	const suffix = "#participant";
	if (!key.startsWith(prefix) || !key.endsWith(suffix)) return null;
	const workspaceId = key.slice(prefix.length, -suffix.length);
	return workspaceId || null;
};
